// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

// Utils
import { scaleToGrid } from '../../../../utils/grid/gridUtils';
import { getAvatarStyle } from '../../../../components/userAvatar/userAvatarUtils';

// Selectors
import { currentBoardIsPublishedSelector } from '../../../../utils/permissions/elementPermissionsSelector';

// Hooks
import useObserveWindowSize from '../../../../utils/dom/useObserveWindowSize';

// Components
import Icon from '../../../../components/icons/Icon';
import BoardEditorAvatars from './BoardEditorAvatars';
import TooltipSource from '../../../../components/tooltips/TooltipSource';

// Constants
import { USER_AVATAR_SIZES } from '../../../../../common/users/userConstants';

// Styles
import './HeaderShareAvatars.scss';

const IPAD_LANDSCAPE_BREAKPOINT = 1370;

const HeaderShareAvatar = ({ children, gridSize, tooltipText, userAvatarSize = USER_AVATAR_SIZES.SMALL }) => {
    const sizePx = Math.ceil(scaleToGrid(userAvatarSize, gridSize));

    return (
        <TooltipSource
            enabled
            className="header-share-avatar-tooltip-source"
            tooltipText={tooltipText}
            pollPosition
            distance={10}
            delay={200}
            triggerOnMouseEnter
        >
            <div className="HeaderShareAvatar" style={getAvatarStyle(sizePx)}>
                <div className="avatar-body">{children}</div>
            </div>
        </TooltipSource>
    );
};

HeaderShareAvatar.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    gridSize: PropTypes.number,
    tooltipText: PropTypes.string,
    userAvatarSize: PropTypes.number,
};

const mapStateToProps = createStructuredSelector({
    currentBoardIsPublished: currentBoardIsPublishedSelector,
});

const HeaderShareAvatars = (props) => {
    const {
        gridSize,
        users,
        userAvatarLimit = undefined,
        userAvatarSize = undefined,
        currentBoardId,
        currentBoardIsPublished,
        dispatchOpenShareModal,
    } = props;

    const [windowWidth] = useObserveWindowSize();

    // Show the user avatars always if the board isn't published or publicly editable
    // or if the window width is larger than the ipad breakpoint
    const showUserAvatars = !currentBoardIsPublished || windowWidth > IPAD_LANDSCAPE_BREAKPOINT;

    // If we should show avatars, let it default to what would normally show
    const userAvatarLimitOverride = showUserAvatars ? userAvatarLimit : 0;

    return (
        <BoardEditorAvatars
            className="HeaderShareAvatars"
            users={users}
            currentBoardId={currentBoardId}
            userAvatarLimit={userAvatarLimitOverride}
            userAvatarSize={userAvatarSize}
            onUserAvatarClick={dispatchOpenShareModal}
            popupId={null}
        >
            {currentBoardIsPublished && (
                <div className="header-published-link-container" onClick={dispatchOpenShareModal}>
                    <HeaderShareAvatar
                        gridSize={gridSize}
                        userAvatarSize={userAvatarSize}
                        tooltipText="Read-only link enabled"
                    >
                        <Icon name="header-published-link" />
                    </HeaderShareAvatar>
                </div>
            )}
        </BoardEditorAvatars>
    );
};

HeaderShareAvatars.propTypes = {
    users: PropTypes.array,
    gridSize: PropTypes.number,
    currentBoardId: PropTypes.string,
    currentBoardIsPublished: PropTypes.bool,
    hasPublicEditPermissions: PropTypes.bool,
    specificUserPermissions: PropTypes.number,
    dispatchOpenShareModal: PropTypes.func,
    userAvatarLimit: PropTypes.number,
    userAvatarSize: PropTypes.number,
};

export default connect(mapStateToProps)(HeaderShareAvatars);
